import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Heart, ArrowRight, ShieldCheck, Users, PawPrint, Loader2, LogIn } from 'lucide-react';
import { useAvailableAnimals, useCampaigns, useShelters, formatHUF, getImageUrl, getAmount, appUrls } from '@rehome/shared';
import type { Animal, DonationCampaign } from '@rehome/shared';

// Cross-app URLs come from the shared config (shared/src/config.ts).
// A menhelyi admin (appUrls.shelter) SZÁNDÉKOSAN nem szerepel a landingen: a
// shelter platform URL-je nem publikus, a menhelyi dolgozók közvetlenül kapják
// meg. A landing az átlagfelhasználóknak szól — ezért a belépés a platformra
// (örökbefogadói fiók) mutat.
const PLATFORM_URL = appUrls.platform;

/**
 * Landing Page — the public face of ReHome (rehome.hu)
 *
 * Sections:
 * 1. Fixed nav bar with logo & CTAs
 * 2. Hero slider (7 rotating slides, 6s interval)
 * 3. Trust badges
 * 4. Random animal spotlights (3 available animals)
 * 5. Urgent campaign preview (top 2 active campaigns)
 */

// ─── Constants ─────────────────────────────────────────────────

const HERO_SLIDES = [
  {
    headline: "A szeretet nem megvásárolható.",
    highlight: "De örökbefogadható.",
    sub: "Ismerd meg védenceinket, akik talán nem tökéletesek, de tökéletesen fognak szeretni téged.",
    image: "https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&q=80&w=1200",
  },
  {
    headline: "Nem csak egy állatot mentesz meg.",
    highlight: "A legjobb barátodat találod meg.",
    sub: "Böngéssz a gazdikeresőink között, és találd meg azt a társat, akinek a személyisége pont hozzád illik.",
    image: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&q=80&w=1200",
  },
  {
    headline: "Valaki pont",
    highlight: "Rád vár.",
    sub: "Ne hagyd, hogy még egy éjszakát a rácsok mögött töltsön – adj neki esélyt a boldog életre még ma.",
    image: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&q=80&w=1200",
  },
  {
    headline: "Légy a hősük,",
    highlight: "Akire egész életükben vártak.",
    sub: "Egyetlen döntés két életet változtat meg: a tiédet, ami teljesebb lesz, és az övét, ami most kezdődik el igazán.",
    image: "https://images.unsplash.com/photo-1591160690555-5debfba289f0?auto=format&fit=crop&q=80&w=1200",
  },
  {
    headline: "Adj esélyt",
    highlight: "Az újrakezdésre.",
    sub: "Minden örökbefogadással nemcsak egy kutyának vagy macskának adsz otthont, hanem helyet szabadítasz fel egy újabb rászorulónak is.",
    image: "https://images.unsplash.com/photo-1519052537078-e6302a4968d4?auto=format&fit=crop&q=80&w=1200",
  },
  {
    headline: "Minden üres kennel egy megmentett élet.",
    highlight: "Segíts kiüríteni őket!",
    sub: "Menhelyeink jelenleg teltházzal működnek. Fogadj örökbe, vagy támogass minket, hogy folytathassuk a mentést!",
    image: "https://images.unsplash.com/photo-1599443015574-be5fe8a05783?auto=format&fit=crop&q=80&w=1200",
  },
  {
    headline: "Otthont keresnek.",
    highlight: "Szeretetet adnak.",
    sub: "Nézd meg az aktuális listánkat, és jelentkezz egy ismerkedő sétára még a héten.",
    image: "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&q=80&w=1200",
  },
];

// ─── Landing Page ─────────────────────────────────────────────

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Fetch from Supabase (falls back to mock data)
  const { data: animals } = useAvailableAnimals();
  const { data: campaigns } = useCampaigns();
  const { data: shelters } = useShelters();

  const [searchQuery, setSearchQuery] = useState('');

  // Dynamic stats from real data
  const shelterCount = (shelters || []).length;
  const animalCount = (animals || []).length;

  // Random animal spotlights — plan.md: "3 véletlenszerű, elérhető állat".
  // Fisher–Yates shuffle, memoized so the picks stay stable within a visit.
  const featuredAnimals = useMemo(() => {
    const available = (animals || []).filter(a => a.status === 'Available');
    const shuffled = [...available];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 3);
  }, [animals]);

  // Active campaigns (already filtered by is_active=true in useCampaigns hook)
  const activeCampaigns = (campaigns || []).slice(0, 2);

  // ─── Hero Slider Logic ──────────────────────────────────────

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const slide = HERO_SLIDES[currentSlide];

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      {/* ── Navigation ──────────────────────────────────────── */}
      <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-2">
              <div className="bg-teal-600 p-2 rounded-lg">
                <PawPrint className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900 tracking-tight">ReHome</span>
            </div>

            <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-600">
              <a href={`${PLATFORM_URL}/animals`} className="hover:text-teal-700 transition-colors">
                Gazdikeresők
              </a>
              <a href={`${PLATFORM_URL}/donate`} className="hover:text-teal-700 transition-colors">
                Adományozás
              </a>
              <a href={`${PLATFORM_URL}/match`} className="hover:text-teal-700 transition-colors">
                Smart Match
              </a>
              <a href={`${PLATFORM_URL}/donate`} className="hover:text-teal-700 transition-colors flex items-center gap-1.5">
                <Heart className="w-4 h-4" />
                Támogatom
              </a>
              {/* A jobb szélső kiemelt gomb a belépés: a landing az átlag
                  felhasználónak szól, akinek az elsődleges teendője a
                  bejelentkezés/regisztráció. Az adományozás megmarad, csak
                  sima linkként — a "Támogatom" a hero és a kampány szekció
                  CTA-iban továbbra is kiemelten szerepel. */}
              <a
                href={`${PLATFORM_URL}/login`}
                className="bg-teal-600 text-white px-5 py-2.5 rounded-lg hover:bg-teal-700 transition-colors shadow-lg shadow-teal-200/50 flex items-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                Felhasználói belépés
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Hero Slider ─────────────────────────────────────── */}
      <section className="relative h-screen min-h-[600px] overflow-hidden">
        {HERO_SLIDES.map((s, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-700 ${
              idx === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={s.image}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 flex items-center h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl" style={{ minHeight: '400px' }}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              {slide.headline}{' '}
              <span className="bg-gradient-to-r from-teal-400 to-emerald-300 bg-clip-text text-transparent">
                {slide.highlight}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed" style={{ minHeight: '80px' }}>
              {slide.sub}
            </p>

            {/* Search bar */}
            <div className="flex gap-3 max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Keresés név, fajta vagy kulcsszó alapján..."
                  className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-white/30 bg-white/10 backdrop-blur-md text-white placeholder-gray-300 focus:outline-none focus:border-teal-400 focus:bg-white/20 transition-all"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      window.location.href = `${PLATFORM_URL}/animals?search=${encodeURIComponent(searchQuery)}`;
                    }
                  }}
                />
              </div>
              <button
                onClick={() => {
                  const dest = searchQuery.trim()
                    ? `${PLATFORM_URL}/animals?search=${encodeURIComponent(searchQuery)}`
                    : `${PLATFORM_URL}/animals`;
                  window.location.href = dest;
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-4 rounded-xl font-semibold transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                Keresés
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Slider indicators */}
            <div className="flex gap-2 mt-8">
              {HERO_SLIDES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  aria-label={`Ugrás a(z) ${idx + 1}. diára`}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    idx === currentSlide ? 'bg-teal-500 w-8' : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust Badges ────────────────────────────────────── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="trust-card bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-7 h-7 text-teal-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{shelterCount} Ellenőrzött Menhely</h3>
              <p className="text-gray-500 text-sm">
                Minden partner menhelyünk átvilágított és rendszeresen ellenőrzött. Állataink chipezve, oltva érkeznek.
              </p>
            </div>

            <div className="trust-card bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-emerald-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{animalCount}+ Gazdikereső Állat</h3>
              <p className="text-gray-500 text-sm">
                Ennyi állat vár jelenleg szerető otthonra a rendszerünkben. Böngéssz közöttük és találd meg a társad!
              </p>
            </div>

            <div className="trust-card bg-white p-8 rounded-2xl shadow-sm text-center">
              <div className="bg-amber-100 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-7 h-7 text-amber-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Utókövetés Minden Állatnak</h3>
              <p className="text-gray-500 text-sm">
                Minden menhely saját follow-up programot kínál. Az örökbefogadás után is melletted vagyunk — a részletekért keresd a menhelyet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Animal Spotlights ────────────────────────────────── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ők most <span className="text-gradient-teal">gazdira várnak</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Közülük három véletlenszerűen kiválasztott védence — lehet, hogy pont az egyikük vár rád!
            </p>
          </div>

          {featuredAnimals.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <PawPrint className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Jelenleg nincs elérhető állat a kiemeléshez.</p>
              <p className="text-sm">Az adatok a Supabase-ből töltődnek be.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredAnimals.map(animal => (
                <a
                  key={animal.id}
                  href={`${PLATFORM_URL}/animals/${animal.id}`}
                  className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:-translate-y-1"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-200">
                    {getImageUrl(animal) ? (
                      <img
                        src={getImageUrl(animal)!}
                        alt={animal.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <PawPrint className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{animal.name}</h3>
                      <span className="text-xs font-medium bg-teal-100 text-teal-700 px-2.5 py-1 rounded-full">
                        {animal.species}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      {animal.breed} · {animal.age ? `${animal.age} éves` : 'Ismeretlen korú'}
                    </p>
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {animal.bio || `${animal.name} szerető otthonra vágyik. Ismerd meg őt közelebbről!`}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <a
              href={`${PLATFORM_URL}/animals`}
              className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-900 transition-colors"
            >
              Összes gazdikereső megtekintése
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ── Urgent Campaigns ─────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Sürgős <span className="text-gradient-teal">gyűjtések</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Ezek a kampányok most a legaktívabbak — minden forint számít!
            </p>
          </div>

          {activeCampaigns.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Heart className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Jelenleg nincs aktív kampány.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {activeCampaigns.map(campaign => {
                const raised = getAmount(campaign, 'raisedAmount', 'raised_amount');
                const target = getAmount(campaign, 'targetAmount', 'target_amount');
                const progress = target > 0
                  ? Math.min(100, Math.round((raised / target) * 100))
                  : 0;
                return (
                  <a
                    key={campaign.id}
                    href={`${PLATFORM_URL}/donate?campaign=${campaign.id}`}
                    className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{campaign.title}</h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                      {campaign.shortDescription || campaign.title}
                    </p>

                    {/* Progress bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-semibold text-teal-700">
                          {formatHUF(raised)}
                        </span>
                        <span className="text-gray-400">
                          {formatHUF(target)}
                        </span>
                      </div>
                      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full animate-progress"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 text-right">{progress}%</p>
                    </div>

                    <div className="flex items-center gap-2 text-teal-700 font-semibold text-sm">
                      Támogatom
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </a>
                );
              })}
            </div>
          )}

          <div className="text-center mt-10">
            <a
              href={`${PLATFORM_URL}/donate`}
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors shadow-lg shadow-teal-200/50"
            >
              <Heart className="w-5 h-5" />
              Összes kampány megtekintése
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <PawPrint className="w-6 h-6 text-teal-400" />
                <span className="text-xl font-bold text-white">ReHome</span>
              </div>
              <p className="text-sm text-gray-400">
                Országos állatmenhely-menedzsment és örökbefogadási platform.
                Küldetésünk, hogy minden állat szerető otthonra találjon.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Navigáció</h4>
              <div className="space-y-2 text-sm">
                <a href={`${PLATFORM_URL}/animals`} className="block hover:text-teal-400 transition-colors">Gazdikeresők</a>
                <a href={`${PLATFORM_URL}/donate`} className="block hover:text-teal-400 transition-colors">Adományozás</a>
                <a href={`${PLATFORM_URL}/match`} className="block hover:text-teal-400 transition-colors">Smart Match</a>
                <a href={`${PLATFORM_URL}/login`} className="block hover:text-teal-400 transition-colors">Felhasználói belépés</a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Jogi</h4>
              <div className="space-y-2 text-sm">
                <a href="#adatkezeles" onClick={e => e.preventDefault()} className="block hover:text-teal-400 transition-colors">Adatkezelési tájékoztató</a>
                <a href="#aszf" onClick={e => e.preventDefault()} className="block hover:text-teal-400 transition-colors">ÁSZF</a>
                <a href="#kapcsolat" onClick={e => e.preventDefault()} className="block hover:text-teal-400 transition-colors">Kapcsolat</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} ReHome by NJE. Minden jog fenntartva.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
